import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import ModalShell from './ModalShell';
import {
  buildReceiptHtml,
  buildReceiptLines,
  copyReceiptText,
  downloadBlob,
  formatReceiptAmount,
  generateReceiptImageFromElement,
  generateReceiptPdfFromElement,
  printReceiptHtml,
  resolveReceiptBranding,
  shareReceiptToMember,
} from '../utils/contributionReceipt';
import {
  attachFileToPersonDocuments,
  buildContributionReceiptDocumentFilename,
} from '../utils/memberDocuments';

/**
 * Modal de reçu : aperçu brandé + QR référence + partage WhatsApp vers le membre.
 * Enregistre automatiquement le PDF dans les documents du membre.
 */
export default function ContributionReceiptModal({
  open,
  onClose,
  settings,
  orgName: orgNameProp,
  logoUrl: logoUrlProp,
  memberName,
  memberPhone = '',
  personId: personIdProp = '',
  contributions = [],
  issuedAt,
  localeFmt = 'fr-FR',
  onToast,
}) {
  const { t } = useTranslation('common');
  const [busy, setBusy] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const previewRef = useRef(null);
  const autoSavedKeyRef = useRef('');

  const branding = useMemo(() => {
    const base = resolveReceiptBranding(settings);
    return {
      ...base,
      orgName: orgNameProp || base.orgName,
      logoUrl: logoUrlProp || base.logoUrl,
    };
  }, [settings, orgNameProp, logoUrlProp]);

  const labels = useMemo(
    () => ({
      title: t('contributionsPage.receipt.title'),
      org: t('contributionsPage.receipt.org'),
      member: t('contributionsPage.receipt.member'),
      issued: t('contributionsPage.receipt.issued'),
      ref: t('contributionsPage.receipt.ref'),
      period: t('contributionsPage.receipt.period'),
      amount: t('contributionsPage.receipt.amount'),
      method: t('contributionsPage.receipt.method'),
      status: t('contributionsPage.receipt.status'),
      notes: t('contributionsPage.receipt.notes'),
      total: t('contributionsPage.receipt.total'),
      currency: t('contributionsPage.receipt.currency'),
      footer: t('contributionsPage.receipt.footer'),
    }),
    [t]
  );

  const rows = useMemo(
    () => (Array.isArray(contributions) ? contributions.filter(Boolean) : []),
    [contributions]
  );
  const total = useMemo(
    () => rows.reduce((s, c) => s + (Number(c?.amount) || 0), 0),
    [rows]
  );

  const personId = useMemo(() => {
    const fromProp = String(personIdProp || '').trim();
    if (fromProp) return fromProp;
    return String(rows[0]?.person_id || rows[0]?.member_id || '').trim();
  }, [personIdProp, rows]);

  const primaryRef = useMemo(() => {
    const ids = rows.map((c) => String(c?.id || '').trim()).filter(Boolean);
    if (!ids.length) return '';
    return ids.length === 1 ? ids[0] : ids.join('|');
  }, [rows]);

  const pdfFilename = useMemo(
    () => buildContributionReceiptDocumentFilename(rows),
    [rows]
  );
  const imageFilename = `recu-cotisation-${String(rows[0]?.id || 'cotisation').slice(0, 12)}.png`;

  useEffect(() => {
    let cancelled = false;
    if (!open || !primaryRef) {
      setQrDataUrl('');
      return undefined;
    }
    (async () => {
      try {
        const url = await QRCode.toDataURL(primaryRef, {
          width: 160,
          margin: 1,
          errorCorrectionLevel: 'M',
          color: { dark: branding.navy || '#16324A', light: '#ffffff' },
        });
        if (!cancelled) setQrDataUrl(url);
      } catch {
        if (!cancelled) setQrDataUrl('');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, primaryRef, branding.navy]);

  const text = useMemo(
    () =>
      buildReceiptLines({
        orgName: branding.orgName,
        memberName,
        contributions: rows,
        issuedAt,
        labels,
        branding,
      }),
    [branding, memberName, rows, issuedAt, labels]
  );

  const notify = (kind, message) => {
    if (typeof onToast === 'function') onToast(kind, message);
  };

  const persistReceiptPdf = async () => {
    if (!personId || !previewRef.current) return { skipped: true };
    const saveKey = `${personId}:${primaryRef || pdfFilename}`;
    if (autoSavedKeyRef.current === saveKey) {
      return { alreadyAttached: true, skipped: true };
    }
    const { file } = await generateReceiptPdfFromElement(previewRef.current, {
      filename: pdfFilename,
    });
    const result = await attachFileToPersonDocuments(personId, file);
    autoSavedKeyRef.current = saveKey;
    return result;
  };

  // Enregistrement auto dans les documents du membre à l’ouverture du reçu.
  useEffect(() => {
    if (!open || !personId || rows.length === 0) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      if (cancelled || !previewRef.current) return;
      try {
        const result = await persistReceiptPdf();
        if (cancelled || result?.skipped) return;
        if (!result?.alreadyAttached) {
          notify('success', t('contributionsPage.receipt.savedToDocuments'));
        }
      } catch {
        if (!cancelled) notify('error', t('contributionsPage.receipt.saveToDocumentsFailed'));
      }
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, personId, primaryRef]);

  if (!open || rows.length === 0) return null;

  const buildPdf = async () => {
    if (!previewRef.current) throw new Error('missing_preview');
    return generateReceiptPdfFromElement(previewRef.current, { filename: pdfFilename });
  };

  const buildImage = async () => {
    if (!previewRef.current) throw new Error('missing_preview');
    return generateReceiptImageFromElement(previewRef.current, { filename: imageFilename });
  };

  const handlePrint = () => {
    const html = buildReceiptHtml({
      orgName: branding.orgName,
      logoUrl: branding.logoUrl,
      memberName,
      contributions: rows,
      issuedAt,
      labels,
      branding,
      qrDataUrl,
    });
    const ok = printReceiptHtml(html);
    if (!ok) notify('error', t('contributionsPage.receipt.printBlocked'));
  };

  const handleDownload = async () => {
    setBusy('download');
    try {
      const { blob, filename } = await buildPdf();
      downloadBlob(blob, filename);
      if (personId) {
        try {
          const result = await persistReceiptPdf();
          if (!result?.skipped && !result?.alreadyAttached) {
            notify('success', t('contributionsPage.receipt.pdfDownloadedAndSaved'));
          } else {
            notify('success', t('contributionsPage.receipt.pdfDownloaded'));
          }
        } catch {
          notify('success', t('contributionsPage.receipt.pdfDownloaded'));
          notify('error', t('contributionsPage.receipt.saveToDocumentsFailed'));
        }
      } else {
        notify('success', t('contributionsPage.receipt.pdfDownloaded'));
      }
    } catch {
      notify('error', t('contributionsPage.receipt.pdfFailed'));
    } finally {
      setBusy('');
    }
  };

  const handleCopy = async () => {
    setBusy('copy');
    try {
      await copyReceiptText(text);
      notify('success', t('contributionsPage.receipt.copied'));
    } catch {
      notify('error', t('contributionsPage.receipt.copyFailed'));
    } finally {
      setBusy('');
    }
  };

  const handleShare = async () => {
    setBusy('share');
    try {
      const { file } = await buildImage();
      const mode = await shareReceiptToMember({
        phone: memberPhone,
        title: t('contributionsPage.receipt.title'),
        text: `${branding.orgName} — ${t('contributionsPage.receipt.title')}`,
        file,
        orgName: branding.orgName,
        memberName,
        reference: primaryRef,
      });
      if (mode === 'whatsapp-clipboard') {
        notify('success', t('contributionsPage.receipt.sharedWhatsappPaste'));
      } else if (mode === 'clipboard') {
        notify('success', t('contributionsPage.receipt.imageCopied'));
      } else {
        notify('success', t('contributionsPage.receipt.imageShared'));
      }
    } catch (err) {
      if (err?.name === 'AbortError') return;
      if (err?.code === 'share_unavailable' || err?.message === 'share_unavailable') {
        notify('error', t('contributionsPage.receipt.shareUnavailable'));
        return;
      }
      notify('error', t('contributionsPage.receipt.shareFailed'));
    } finally {
      setBusy('');
    }
  };

  const contactLine = [branding.address, branding.phone, branding.email]
    .filter(Boolean)
    .join(' · ');
  const initial = String(branding.orgName || 'E').trim().slice(0, 1).toUpperCase() || 'E';

  return (
    <ModalShell
      maxWidthClass="max-w-lg"
      zClass="z-[60]"
      onBackdropClick={onClose}
      panelClassName="flex min-h-0 flex-col overflow-hidden p-0"
    >
      <div className="mm-modal-header">
        <div className="min-w-0">
          <p className="mm-hero__label" style={{ color: branding.primary }}>
            {t('contributionsPage.receipt.kicker')}
          </p>
          <h3 className="mt-0.5 truncate font-display text-lg font-extrabold tracking-tight text-[#0f3350]">
            {t('contributionsPage.receipt.previewTitle')}
          </h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="mm-press flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100/80 text-slate-500"
          aria-label={t('contributionsPage.receipt.closeAria')}
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto bg-[#eef3f8] px-3 py-4 sm:px-5">
        <div
          ref={previewRef}
          className="mx-auto max-w-[420px] overflow-hidden rounded-2xl bg-white shadow-[0_12px_40px_rgba(22,50,74,0.14)]"
        >
          <div
            className="relative px-5 pb-5 pt-5 text-white"
            style={{
              background: `linear-gradient(135deg, ${branding.primaryDark} 0%, ${branding.primary} 58%, ${branding.sky} 145%)`,
            }}
          >
            <div
              className="pointer-events-none absolute -right-8 -top-10 h-28 w-28 rounded-full opacity-20"
              style={{ background: '#fff' }}
            />
            <div className="relative flex items-start gap-3.5">
              {branding.logoUrl ? (
                <img
                  src={branding.logoUrl}
                  alt=""
                  className="h-14 w-14 shrink-0 rounded-2xl bg-white object-contain p-1.5 shadow-sm"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const fallback = e.currentTarget.nextElementSibling;
                    if (fallback) fallback.classList.remove('hidden');
                  }}
                />
              ) : null}
              <div
                className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white text-xl font-extrabold shadow-sm ${
                  branding.logoUrl ? 'hidden' : ''
                }`}
                style={{ color: branding.primary }}
                aria-hidden
              >
                {initial}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/85">
                  {labels.title}
                </p>
                <p className="mt-0.5 text-[15px] font-bold leading-snug break-words">
                  {branding.orgFullName || branding.orgName}
                </p>
                {branding.orgFullName && branding.orgName ? (
                  <p className="mt-0.5 text-xs leading-snug text-white/85 break-words">{branding.orgName}</p>
                ) : null}
              </div>
            </div>
            {contactLine ? (
              <p className="relative mt-3 text-[11px] leading-snug text-white/85">{contactLine}</p>
            ) : null}
          </div>

          <div className="space-y-3 px-5 py-4">
            <div
              className="grid grid-cols-2 gap-3 rounded-xl border px-3.5 py-3"
              style={{ borderColor: branding.line, background: branding.primarySoft }}
            >
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: branding.muted }}>
                  {labels.member}
                </p>
                <p className="mt-0.5 text-sm font-semibold" style={{ color: branding.text }}>
                  {memberName || '—'}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: branding.muted }}>
                  {labels.issued}
                </p>
                <p className="mt-0.5 text-sm font-medium" style={{ color: branding.text }}>
                  {issuedAt || '—'}
                </p>
              </div>
            </div>

            {primaryRef && qrDataUrl ? (
              <div
                className="flex items-center gap-3 rounded-xl border px-3.5 py-3"
                style={{ borderColor: branding.line }}
              >
                <img
                  src={qrDataUrl}
                  alt={t('contributionsPage.receipt.qrAlt')}
                  className="h-[72px] w-[72px] shrink-0 rounded-lg bg-white"
                />
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: branding.muted }}>
                    {t('contributionsPage.receipt.qrLabel')}
                  </p>
                  <p className="mt-1 break-all text-[11px] font-medium leading-snug" style={{ color: branding.text }}>
                    {primaryRef}
                  </p>
                </div>
              </div>
            ) : null}

            {rows.map((c, idx) => (
              <article
                key={c.id || `${c.month}-${idx}`}
                className="rounded-xl border px-3.5 py-3"
                style={{ borderColor: branding.line }}
              >
                {rows.length > 1 && (
                  <p className="mb-2 text-[10px] font-bold uppercase tracking-wide" style={{ color: branding.primary }}>
                    #{idx + 1}
                  </p>
                )}
                {c.id ? (
                  <p className="mb-2 break-all text-[10px]" style={{ color: branding.muted }}>
                    {labels.ref} {c.id}
                  </p>
                ) : null}
                <div className="flex items-end justify-between gap-3">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: branding.muted }}>
                      {labels.period}
                    </p>
                    <p className="text-sm font-semibold" style={{ color: branding.text }}>
                      {c.periodLabel || c.month || '—'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: branding.muted }}>
                      {labels.amount}
                    </p>
                    <p className="text-xl font-extrabold tabular-nums leading-none" style={{ color: branding.primaryDark }}>
                      {formatReceiptAmount(c.amount, localeFmt)}
                      <span className="ml-1 text-[10px] font-semibold" style={{ color: branding.muted }}>
                        {labels.currency}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 border-t pt-2.5" style={{ borderColor: branding.line }}>
                  <div>
                    <p className="text-[10px] uppercase tracking-wide" style={{ color: branding.muted }}>
                      {labels.method}
                    </p>
                    <p className="text-xs font-medium" style={{ color: branding.text }}>
                      {c.payment_method || '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wide" style={{ color: branding.muted }}>
                      {labels.status}
                    </p>
                    <p className="text-xs font-medium" style={{ color: branding.text }}>
                      {c.statusLabel || c.status || '—'}
                    </p>
                  </div>
                </div>
                {c.notes ? (
                  <p className="mt-2 text-xs" style={{ color: branding.muted }}>
                    <span className="font-semibold">{labels.notes} :</span> {c.notes}
                  </p>
                ) : null}
              </article>
            ))}

            {rows.length > 1 && (
              <p className="text-right text-sm font-extrabold tabular-nums" style={{ color: branding.primaryDark }}>
                {labels.total} : {formatReceiptAmount(total, localeFmt)} {labels.currency}
              </p>
            )}

            <div
              className="border-t border-dashed pt-3 text-[11px] leading-snug"
              style={{ borderColor: branding.line, color: branding.muted }}
            >
              {labels.footer}
              {branding.registrationNumber ? (
                <span className="mt-1 block">
                  {t('contributionsPage.receipt.regNumber', { number: branding.registrationNumber })}
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className="mm-modal-footer grid grid-cols-2 gap-2 sm:grid-cols-4">
        <button
          type="button"
          onClick={handleShare}
          disabled={!!busy}
          className="mm-cta disabled:opacity-60"
          style={{ background: branding.primary }}
        >
          {busy === 'share' ? '…' : t('contributionsPage.receipt.share')}
        </button>
        <button
          type="button"
          onClick={handlePrint}
          disabled={!!busy}
          className="mm-cta mm-cta--ghost disabled:opacity-60"
        >
          {t('contributionsPage.receipt.print')}
        </button>
        <button
          type="button"
          onClick={handleCopy}
          disabled={!!busy}
          className="mm-cta mm-cta--ghost disabled:opacity-60"
        >
          {busy === 'copy' ? '…' : t('contributionsPage.receipt.copy')}
        </button>
        <button
          type="button"
          onClick={handleDownload}
          disabled={!!busy}
          className="mm-cta mm-cta--ghost disabled:opacity-60"
        >
          {busy === 'download' ? '…' : t('contributionsPage.receipt.downloadPdf')}
        </button>
      </div>
    </ModalShell>
  );
}
